import {
  Wrench,
  Activity,
  Thermometer,
  ShieldCheck,
  PlugZap,
  ClipboardCheck,
  Cog,
  Gauge,
  Flame,
  Cpu,
  RotateCcw,
  Siren,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "ppm",
    icon: Wrench,
    title: "Planned Preventive Maintenance",
    short: "PPM programmes that keep critical assets running.",
    blurb:
      "Scheduled inspections, preventive tasks and predictive techniques tailored to your industry and operational needs — reducing unplanned downtime and extending equipment lifespan.",
    bullets: [
      "Tailored maintenance schedules",
      "Predictive techniques & data logs",
      "Reduced unplanned downtime",
      "Extended asset lifecycle",
    ],
  },
  {
    slug: "vibration-reports",
    icon: Activity,
    title: "Vibration Reports",
    short: "Catch failure modes before they cost you a shift.",
    blurb:
      "Advanced vibration analysis detects bearing wear, imbalance and misalignment early. We deliver expert reports with actionable recommendations and trend baselines.",
    bullets: [
      "Early fault detection",
      "Bearing, imbalance & alignment",
      "Trend baselines & history",
      "Actionable engineer recommendations",
    ],
  },
  {
    slug: "thermal-imaging",
    icon: Thermometer,
    title: "Thermal Imaging Reports",
    short: "See hot-spots before they become outages.",
    blurb:
      "FLIR / Fluke thermography surveys for switchgear, motors and process plant — pinpointing loose connections, overloads and insulation breakdown.",
    bullets: [
      "Electrical & mechanical thermography",
      "Insurance-ready reports",
      "Energy efficiency insights",
      "Safety risk reduction",
    ],
  },
  {
    slug: "electrical-inspection",
    icon: ShieldCheck,
    title: "Electrical Inspection & Testing",
    short: "EICRs and compliance to the latest BS 7671.",
    blurb:
      "Qualified electricians carry out periodic inspection & testing, delivering full reports detailing defects, deficiencies and remedial actions.",
    bullets: [
      "EICR — commercial & industrial",
      "BS 7671 compliance",
      "Full remedial reporting",
      "Risk & safety mitigation",
    ],
  },
  {
    slug: "electrical-installation",
    icon: PlugZap,
    title: "Electrical Installation",
    short: "Industrial power, controls & integration.",
    blurb:
      "Bespoke installations for industrial and commercial sectors — power distribution, machinery integration, control systems and lighting, compliant with BS 7671 and DSEAR / ATEX.",
    bullets: [
      "Power distribution",
      "Machinery integration",
      "Control systems & panels",
      "ATEX / DSEAR compliant zones",
    ],
  },
  {
    slug: "pat-testing",
    icon: ClipboardCheck,
    title: "PAT Testing",
    short: "Portable appliance testing, done properly.",
    blurb:
      "Thorough inspection and testing of portable appliances to keep your workplace compliant and your people safe — efficient on-site delivery.",
    bullets: [
      "On-site testing",
      "Asset register & labelling",
      "Pass / fail certificates",
      "Workplace compliance",
    ],
  },
  {
    slug: "fire-alarms-emergency-lighting",
    icon: Siren,
    title: "Fire Alarms & Emergency Lighting",
    short: "Life-safety systems — installed, tested and maintained.",
    blurb:
      "Design, install, periodic testing and certification of fire alarm and emergency lighting systems for industrial and commercial premises — keeping you compliant with BS 5839 and BS 5266.",
    bullets: [
      "Fire alarm install & upgrades",
      "Emergency lighting systems",
      "Periodic testing & certification",
      "Reactive call-out & repairs",
    ],
  },
  {
    slug: "bespoke-machinery",
    icon: Cog,
    title: "Design, Manufacture & Commissioning",
    short: "Bespoke machinery from sketch to shop floor.",
    blurb:
      "Innovation, precision engineering and tailored solutions — we design, manufacture and commission bespoke machinery aligned to your exact process requirements.",
    bullets: [
      "Concept & feasibility",
      "Mechanical & electrical design",
      "In-house manufacture",
      "Commissioning & handover",
    ],
  },
  {
    slug: "mechanical-services",
    icon: Gauge,
    title: "Mechanical, Hydraulic & Pneumatic Services",
    short: "Hydraulics, pneumatics and water systems — built and maintained.",
    blurb:
      "Full mechanical engineering coverage — hydraulic power packs and cylinders, pneumatic and compressed-air systems, water and process pipework. Design, install, fault-find and maintain.",
    bullets: [
      "Hydraulic systems & power packs",
      "Pneumatic & compressed-air systems",
      "Water & process pipework",
      "Pumps, valves & instrumentation",
    ],
  },
  {
    slug: "welding-fabrication",
    icon: Flame,
    title: "Welding & Fabrication",
    short: "Steel, stainless and aluminium — fabricated in-house.",
    blurb:
      "Coded welders and skilled fabricators delivering everything from bracketry and guarding to full machine frames — across mild steel, stainless steel and aluminium.",
    bullets: [
      "MIG, TIG and stick welding",
      "Mild steel, stainless & aluminium",
      "Frames, guarding & bespoke parts",
      "On-site & in-workshop fabrication",
    ],
  },
  {
    slug: "automation-controls",
    icon: Cpu,
    title: "Automation & Controls",
    short: "PLC programming, HMI design and full automation builds.",
    blurb:
      "PLC-led control systems — from a single retrofit panel to a fully bespoke automated cell. PLC logic, HMI screens and SCADA integration written and proven by Lutrom engineers.",
    bullets: [
      "PLC programming",
      "HMI design & programming",
      "Control cabinet builds",
      "Automation retrofits & upgrades",
    ],
  },
  {
    slug: "machine-refurbishment",
    icon: RotateCcw,
    title: "Machine Refurbishment",
    short: "Bring tired plant back to factory condition — and beyond.",
    blurb:
      "Full mechanical and electrical refurbishment of existing assets — strip-down, repair, modernise and re-commission. Extend asset life and bring controls up to current standards.",
    bullets: [
      "Mechanical strip & overhaul",
      "Electrical & control upgrades",
      "Compliance & safety refresh",
      "Re-commission & sign-off",
    ],
  },
];

export const STATS = [
  { value: "24/7", label: "On-call response across the North West" },
  { value: "<2 hr", label: "Typical breakdown response window" },
  { display: "Industrial & Commercial", subtitle: "Sectors served" },
];

export const SECTORS = [
  "Manufacturing",
  "Food & Beverage",
  "Pharmaceutical",
  "Chemical & Process",
  "Plastics & Polymer",
  "Textiles",
  "Logistics & Warehousing",
  "Recycling & Waste",
  "Football Stadiums",
  "Schools & Education",
  "Retail Stores",
  "Hospitality & Leisure",
  "Healthcare",
  "Offices & Workplaces",
];
